import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PixieDustAnimation from "../../components/agent/PixieDustAnimation";
import { getPixieNext, clearPixieNext } from "../../utils/pixieDustNav";

// Array of magical quotes (constant, defined outside component)
const QUOTES = [
  { text: '"Here we gooo!"', author: "Peter Pan", color: "text-gray-700" },
  {
    text: '"Every adventure requires a first step"',
    author: "Alice in Wonderland",
    color: "text-gray-700",
  },
  { text: '"Go the distance"', author: "Hercules", color: "text-gray-700" },
  {
    text: '"It all started with a mouse"',
    author: "Walt Disney",
    color: "text-gray-700",
  },
  {
    text: '"Here you leave today and enter the world of yesterday, tomorrow, and fantasy"',
    author: "Magic Kingdom",
    color: "text-gray-700",
  },
];

const PixieDustAnimationPage = () => {
  const navigate = useNavigate();
  // Animation always starts immediately on mount (no effect needed)
  const [showAnimation] = useState(true);
  const [showModal, setShowModal] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [animationDuration] = useState(8000); // 8 seconds
  // Initialize random quote once using lazy initializer (avoids effect)
  const [randomQuote] = useState(() => {
    const randomIndex = Math.floor(Math.random() * QUOTES.length);
    return QUOTES[randomIndex];
  });
  
  // Guard against StrictMode double effects
  const didScheduleRef = useRef(false);
  const navigationScheduledRef = useRef(false);

  const handleAnimationComplete = useCallback(() => {
    // Prevent double navigation in StrictMode
    if (navigationScheduledRef.current) {
      return;
    }
    navigationScheduledRef.current = true;

    // Start fade-out transition immediately
    setIsFadingOut(true);
    setShowModal(false);
    
    // Get destination from canonical storage
    const nextPath = getPixieNext();
    
    // Determine final destination with safe fallback
    const finalDestination = nextPath || '/agent/crm/trips';
    
    // Clear storage BEFORE navigating to prevent replay loops
    clearPixieNext();
    
    // Navigate after fade-out completes (~200ms)
    setTimeout(() => {
      navigate(finalDestination, { replace: true });
    }, 200);
  }, [navigate]);

  // Auto-hide modal after 3 seconds to reveal the animation
  useEffect(() => {
    // Guard against StrictMode double effects
    if (didScheduleRef.current) {
      return;
    }
    didScheduleRef.current = true;
    
    const modalTimer = setTimeout(() => {
      setShowModal(false);
    }, 3000);
    
    return () => clearTimeout(modalTimer);
  }, []); // Empty deps - run once on mount

  return (
    <div 
      className={`flex fixed inset-0 z-[9999] justify-center items-center bg-black bg-opacity-75 transition-opacity duration-200 ${
        isFadingOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Peter Pan Quote Modal */}
      {showModal && (
        <div className="relative z-[10000] p-8 mx-4 w-full max-w-md text-center bg-white rounded-2xl shadow-2xl transition-all duration-300 transform scale-100 opacity-100">
          <div className="flex flex-col items-center">
            {/* Random Quote */}
            {randomQuote && (
              <>
                <h2
                  className={`mb-4 text-3xl font-bold ${randomQuote.color} font-serif italic tracking-wide`}
                  style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                >
                  {randomQuote.text}
                </h2>
                <p className="mb-6 font-serif text-lg italic font-medium text-gray-700">
                  - {randomQuote.author}
                </p>
              </>
            )}
            <p className="mb-6 text-sm leading-relaxed text-center text-gray-600">
              Your magical itinerary is being prepared with pixie dust...
            </p>

            {/* Magical Elements */}
            <div className="flex flex-col items-center space-y-3 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                <span>Gathering your park adventures</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                <span>Adding magical touches</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                <span>Creating unforgettable memories</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pixie Dust Animation */}
      {showAnimation && (
        <PixieDustAnimation
          onComplete={handleAnimationComplete}
          duration={animationDuration}
        />
      )}
    </div>
  );
};

export default PixieDustAnimationPage;